import { useEffect, useRef } from "react";
import gsap from "gsap";
import { Check } from "lucide-react";
import { ParticleHero } from "./ParticleHero";
import { RegistrationForm } from "./RegistrationForm";
import { BONUSES, WEBINAR, HOST } from "../lib/content";
import { scrollToId } from "../lib/anim";

export const Hero = ({ attribution }) => {
  const h1Ref = useRef(null);

  useEffect(() => {
    const lines = h1Ref.current.querySelectorAll(".h1-line");
    gsap.fromTo(
      lines,
      { yPercent: 110, opacity: 0 },
      { yPercent: 0, opacity: 1, duration: 1, ease: "power4.out", stagger: 0.12, delay: 0.3 }
    );
  }, []);

  return (
    <section id="hero" className="relative min-h-screen grid md:grid-cols-2 pt-24 md:pt-0" data-testid="hero-section">
      <div className="relative z-10 flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 md:py-28">
        <p className="font-mono text-[10px] tracking-[0.28em] text-[#FFFFFF] mb-6" data-testid="hero-eyebrow">
          {WEBINAR.eyebrow}
        </p>
        <h1 ref={h1Ref} className="font-display uppercase tracking-[-0.03em] leading-[0.95] mb-7" data-testid="hero-h1">
          <span className="block overflow-hidden"><span className="h1-line block text-[clamp(34px,4.6vw,68px)] text-[#D9E4F5]">STOP LETTING DEBT</span></span>
          <span className="block overflow-hidden"><span className="h1-line block text-[clamp(34px,4.6vw,68px)] text-[#FFFFFF]">CONTROL YOUR FUTURE.</span></span>
          <span className="block overflow-hidden"><span className="h1-line block text-[clamp(21px,2.9vw,42px)] text-[#D9E4F5]/60">PAY OFF DEBT IN AS LITTLE AS 9 YEARS OR LESS — AND KEEP BUILDING AFTER IT'S GONE</span></span>
        </h1>
        <p className="text-[15px] text-white/55 leading-relaxed max-w-lg mb-6">
          See if you <span className="text-[#FFFFFF]">qualify</span> for Debt-Free Life — a financial strategy that may help qualified clients pay off their mortgage, credit cards, and student loans in as little as{" "}
          <span className="text-[#FFFFFF]">9 years or less</span>, while building cash value that keeps working long after the debt is gone.
        </p>
        <blockquote className="border-l-2 border-[#FFFFFF] pl-5 italic text-[14px] text-white/60 max-w-md mb-8">
          What if the payments you're already making could pay off your debt sooner — and then keep building your future?
        </blockquote>
        <div className="mb-3">
          <button
            data-testid="hero-cta-btn"
            data-cta="true"
            onClick={() => scrollToId("#register")}
            className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#0E2A54] font-bold text-sm tracking-wide px-8 py-4 hover:bg-[#DCE8FF] active:scale-[0.98] transition-[background-color,transform] duration-200"
          >
            <Check className="w-4 h-4" /> BOOK MY FREE CALL
          </button>
        </div>
        <p className="font-mono text-[11px] text-white/25 mb-12">Personalized review · Qualification required · Results vary</p>

        <div className="flex items-center gap-4" data-testid="hero-host-module">
          <img
            src={HOST.portrait}
            alt="Najiyyah Qaasim"
            className="w-12 h-12 rounded-full object-cover border-2 border-[#FFFFFF]"
          />
          <div>
            <p className="text-sm font-semibold text-[#D9E4F5]">{HOST.name}</p>
            <p className="font-mono text-[10px] text-white/40">{HOST.company} · License # {HOST.license}</p>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center px-6 md:px-10 py-10 md:py-28">
        <ParticleHero />
        <div className="relative z-10 w-full max-w-md border border-white/12 bg-[#091E42]/45 backdrop-blur-xl p-6 md:p-8" data-testid="hero-registration-card">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#FFFFFF] mb-2">SEE IF YOU QUALIFY</p>
          <h3 className="text-lg font-bold text-[#D9E4F5] mb-4">When You Book, You'll Receive:</h3>
          <ul className="space-y-2.5 mb-5">
            {BONUSES.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-[13px] text-white/70">
                <Check className="w-4 h-4 text-[#FFFFFF] shrink-0 mt-0.5" /> {b}
              </li>
            ))}
          </ul>
          <p className="font-display text-[22px] text-[#FFFFFF] mb-5">FREE!</p>
          <RegistrationForm variant="hero" attribution={attribution} />
        </div>
      </div>
    </section>
  );
};
